// Global Instructions Rule Applied!

/**
 * usePatchRequest Hook
 *
 * This hook handles automatic PATCH requests for the InputManager component.
 * It parses field keys to extract API paths and handles authentication.
 */

import { useState, useCallback } from 'react'
import { supabase } from '../../../lib/supabase'
import { UsePatchRequestReturn } from '../types'
import { getBackendBaseUrl } from '../../../utils/endpoints.ts'

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for handling PATCH requests in InputManager
 * @returns Object with patch function and state
 */
export const usePatchRequest = (): UsePatchRequestReturn => {
  const [isPatching, setIsPatching] = useState<boolean>(false)
  const [patchError, setPatchError] = useState<Error | null>(null)

  /**
   * Parse field key to extract API path and field name
   * @param fieldKey - Field key in format "path__fieldname" or just "fieldname"
   * @returns Object with path and field name
   */
  const parseFieldKey = useCallback((fieldKey: string): { path: string; fieldName: string } => {
    // First, strip away everything before the last dash (e.g., "471257b1-d02a-4536-9e1a-b87e0be9733b-deals" -> "deals")
    const lastDashIndex = fieldKey.lastIndexOf('-')
    const cleanedFieldKey = lastDashIndex !== -1 ? fieldKey.substring(lastDashIndex + 1) : fieldKey

    if (cleanedFieldKey.includes('__')) {
      const [path, fieldName] = cleanedFieldKey.split('__', 2)
      return { path, fieldName }
    }

    // If no double underscore, assume it's just a field name
    // This would need to be handled by the parent component providing the path
    return { path: '', fieldName: cleanedFieldKey }
  }, [])

  /**
   * Get authentication headers with JWT token
   * @returns Promise with headers object
   */
  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    try {
      const {
        data: { session }
      } = await supabase.auth.getSession()

      if (!session?.access_token) {
        throw new Error('No authentication token available')
      }

      return {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      }
    } catch (error) {
      console.error('Error getting auth headers:', error)
      throw new Error('Failed to get authentication token')
    }
  }, [])

  /**
   * Execute a PATCH request for a field
   * @param fieldKey - Field key in format "path__fieldname"
   * @param value - New value for the field
   * @param patchId - ID for the patch request
   * @param patchFields - Optional field mappings for multi-field PATCH requests
   * @param patchEndpoint - Optional endpoint override
   * @param valueTransformer - Optional function to transform value and return complete field set
   * @param currentRecord - Current record data for valueTransformer
   * @returns Promise that resolves with response data when request completes
   */
  const patchField = useCallback(
    async (
      fieldKey: string,
      value: any,
      patchId: string | number,
      patchFields?: Array<{ sourceKey: string; targetField: string }>,
      patchEndpoint?: string,
      valueTransformer?: (value: any, currentRecord: any) => Record<string, any>,
      currentRecord?: any
    ): Promise<Record<string, any> | void> => {
      setIsPatching(true)
      setPatchError(null)

      try {
        // Determine the API path
        // If patchEndpoint is empty string, skip the patch (used to disable auto-patch for specific fields)
        if (patchEndpoint === '') {
          return undefined
        }

        let path: string
        if (patchEndpoint) {
          path = patchEndpoint
        } else {
          const parsed = parseFieldKey(fieldKey)
          if (!parsed.path) {
            throw new Error(`Invalid field key format: ${fieldKey}. Expected format: "path__fieldname"`)
          }
          path = parsed.path
        }

        // Get base URL from environment
        const baseUrl = getBackendBaseUrl()

        // Build the request URL
        const url = `${baseUrl}/${path}`

        // Get authentication headers
        const headers = await getAuthHeaders()

        // Prepare request body based on whether we have valueTransformer or multi-field mapping
        let body: Record<string, any> = { id: patchId }

        if (valueTransformer && currentRecord) {
          // Value transformer: get complete field set from transformer
          const transformedFields = valueTransformer(value, currentRecord)

          // If valueTransformer includes an id, use it to override patchId
          if (transformedFields.id) {
            body.id = transformedFields.id
          }

          if (patchFields && Array.isArray(patchFields) && patchFields.length > 0) {
            // Map transformed fields to API field names using patchFields
            // When patchFields is used, fields with '__' are Redux-only and should be filtered out
            patchFields.forEach(({ sourceKey, targetField }) => {
              if (sourceKey in transformedFields) {
                body[targetField] = transformedFields[sourceKey]
              }
            })
          } else {
            // If no patchFields, use transformed fields directly
            // Filter out Redux-only fields (those with '__' that are NOT prog__*)
            // prog__* fields are API fields (e.g., prog__account_roles) and should be included
            Object.keys(transformedFields).forEach((key) => {
              // Exclude 'id' if it's already set (to avoid overriding patchId)
              if (key === 'id' && body.id) {
                return
              }

              // Special handling for userFields array (used by contact_accounts relationships)
              // userFields contains objects with '__' field names but should be included as-is
              if (key === 'userFields' && Array.isArray(transformedFields[key])) {
                body[key] = transformedFields[key]
                return
              }

              // Include fields that:
              // 1. Don't have '__' (regular API fields)
              // 2. Start with 'prog__' (API fields like prog__account_roles)
              // Exclude other fields with '__' (Redux-only fields like accounts__business_country_label)
              if (!key.includes('__') || key.startsWith('prog__')) {
                body[key] = transformedFields[key]
              }
            })
          }
        } else if (patchFields && Array.isArray(patchFields) && patchFields.length > 0) {
          // Multi-field PATCH: map value object to multiple API fields
          patchFields.forEach(({ sourceKey, targetField }) => {
            if (value && typeof value === 'object' && sourceKey in value) {
              body[targetField] = value[sourceKey]
            }
          })
        } else {
          // Single-field PATCH: use the original logic
          const { fieldName } = parseFieldKey(fieldKey)
          if (!fieldName) {
            throw new Error(`Invalid field key format: ${fieldKey}. Expected format: "path__fieldname"`)
          }
          // A cleared input can arrive as `undefined`: an AntD Select with
          // allowClear emits undefined where the date / number / time inputs
          // emit null. JSON.stringify drops an undefined key, so the request
          // carried the id alone and the backend had no column to update (the
          // fund Terms tab answered 400 "upper bound of FOR loop cannot be
          // null"). A confirmed clear means "blank this column" - send an
          // explicit null. The valueTransformer / patchFields branches above
          // build their own field sets and are left as they are.
          body[fieldName] = value === undefined ? null : value
        }

        // Execute the PATCH request
        const response = await fetch(url, {
          method: 'PATCH',
          headers,
          body: JSON.stringify(body)
        })

        // Check if request was successful
        if (!response.ok) {
          let errorMessage = `PATCH request failed: ${response.status} ${response.statusText}`

          try {
            const errorData = await response.json()
            if (errorData.message) {
              errorMessage = errorData.message
            } else if (errorData.error) {
              errorMessage = errorData.error
            }
          } catch {
            // If we can't parse error response, use the default message
          }

          throw new Error(errorMessage)
        }

        // Parse and return response data
        try {
          const responseData = await response.json()
          return responseData
        } catch {
          // If response is empty or not JSON, return undefined
          return undefined
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred'
        const patchError = new Error(`Failed to update field ${fieldKey}: ${errorMessage}`)

        console.error('PATCH request failed:', patchError)
        setPatchError(patchError)
        throw patchError
      } finally {
        setIsPatching(false)
      }
    },
    [parseFieldKey, getAuthHeaders]
  )

  return {
    patchField,
    isPatching,
    patchError
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Validate if a field key supports automatic patching
 * @param fieldKey - Field key to validate
 * @returns True if field key has the correct format for patching
 */
const isValidPatchFieldKey = (fieldKey: string): boolean => {
  return fieldKey.includes('__') && fieldKey.split('__').length === 2
}

/**
 * Extract API path from field key
 * @param fieldKey - Field key in format "path__fieldname"
 * @returns API path or null if invalid format
 */
const extractApiPath = (fieldKey: string): string | null => {
  if (!isValidPatchFieldKey(fieldKey)) {
    return null
  }

  const [path] = fieldKey.split('__', 2)
  return path
}

/**
 * Extract field name from field key
 * @param fieldKey - Field key in format "path__fieldname"
 * @returns Field name or null if invalid format
 */
const extractFieldName = (fieldKey: string): string | null => {
  if (!isValidPatchFieldKey(fieldKey)) {
    return null
  }

  const [, fieldName] = fieldKey.split('__', 2)
  return fieldName
}

/**
 * Extract full field name from field key (including table prefix)
 * @param fieldKey - Field key in format "uuid-path__fieldname" or "path__fieldname"
 * @returns Full field name (e.g., "deals__allocation_available") or null if invalid format
 */
export const extractFullFieldName = (fieldKey: string): string | null => {
  // First, strip away everything before the last dash (e.g., "uuid-deals__allocation_available" -> "deals__allocation_available")
  const lastDashIndex = fieldKey.lastIndexOf('-')
  const cleanedFieldKey = lastDashIndex !== -1 ? fieldKey.substring(lastDashIndex + 1) : fieldKey

  // Check if it contains the double underscore pattern
  if (cleanedFieldKey.includes('__')) {
    return cleanedFieldKey
  }

  return null
}
